import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ChaveEtapaKanbanEnum } from '@shared/enums/vendas/chave-etapa-kanban.enum';
import { VendaTipoIntegracaoEnum, VendaTipoIntegracaoIndicador } from '@shared/enums/vendas/venda-tipo-integracao.enum';
import { MoverVendaEtapaIntegracaoStruct } from '@shared/services/structs/vendas/mover-venda-etapa-integracao.struct';

export interface PvConfirmarMovimentoDialogData {
    /** "Voltar para {etapa}" ou "Cancelar pedido". */
    titulo: string;
    /** O destino é a etapa Cancelado: muda o ícone e o botão de confirmar. */
    isCancelar: boolean;
    /** "Pedido de venda nº 1.009", no subtítulo do cabeçalho. */
    rotuloDocumento: string;
    /** Integrações do pedido, já completadas pelo servidor com permissão e bloqueios do Finanças. */
    integracoes: MoverVendaEtapaIntegracaoStruct[];
}

export interface PvConfirmarMovimentoDialogResult {
    /** O que o usuário marcou para desfazer antes de mover (VendaTipoIntegracaoEnum). */
    idsVendaTipoIntegracaoDesfazer: number[];
}

/** Título e ícone do alerta: cancelar é o movimento para a etapa Cancelado; o resto é voltar etapa. */
export function cabecalhoDoMovimento(chaveDestino: string | null, nomeDestino: string): { titulo: string; isCancelar: boolean } {
    const isCancelar = chaveDestino === ChaveEtapaKanbanEnum.Cancelado;
    return { titulo: isCancelar ? 'Cancelar pedido' : `Voltar para ${nomeDestino}`, isCancelar };
}

/**
 * Alerta de voltar etapa ou cancelar um pedido com integrações (T17).
 *
 * O movimento não é travado: o usuário confirma que quer mover e escolhe o que desfazer (financeiro, estoque). O que
 * não pode ser desfeito (nota, financeiro recebido ou bloqueado, sem permissão, lançamento que a etapa de destino
 * refaz pela automação) aparece desabilitado, com o motivo; o servidor confere de novo ao confirmar. Fechar sem
 * confirmar devolve `undefined`, e o shell interpreta isso como desistência — o card volta para a coluna de origem.
 */
@Component({
    selector: 'app-pv-confirmar-movimento-dialog',
    standalone: true,
    imports: [CommonModule, MatDialogModule, MatButtonModule, MatCheckboxModule, MatIconModule],
    templateUrl: './pv-confirmar-movimento-dialog.component.html',
    styleUrls: ['./pv-confirmar-movimento-dialog.component.css'],
})
export class PvConfirmarMovimentoDialogComponent {
    private readonly selecionadas = new Set<number>();

    constructor(
        public dialogRef: MatDialogRef<PvConfirmarMovimentoDialogComponent, PvConfirmarMovimentoDialogResult>,
        @Inject(MAT_DIALOG_DATA) public data: PvConfirmarMovimentoDialogData,
    ) {}

    /** "Desfazer o financeiro", "Desfazer o estoque", "Desfazer a NF-e". */
    public rotulo(integracao: MoverVendaEtapaIntegracaoStruct): string {
        const nome = VendaTipoIntegracaoIndicador[integracao.idVendaTipoIntegracao]?.nome ?? 'lançamento';
        const isNota = integracao.idVendaTipoIntegracao !== VendaTipoIntegracaoEnum.Financeiro
            && integracao.idVendaTipoIntegracao !== VendaTipoIntegracaoEnum.Estoque;
        return isNota ? `Desfazer a ${nome}` : `Desfazer o ${nome.toLowerCase()}`;
    }

    public isSelecionada(integracao: MoverVendaEtapaIntegracaoStruct): boolean {
        return this.selecionadas.has(integracao.idVendaTipoIntegracao);
    }

    public alternar(integracao: MoverVendaEtapaIntegracaoStruct): void {
        if (this.selecionadas.has(integracao.idVendaTipoIntegracao)) this.selecionadas.delete(integracao.idVendaTipoIntegracao);
        else this.selecionadas.add(integracao.idVendaTipoIntegracao);
    }

    public confirmar(): void {
        this.dialogRef.close({ idsVendaTipoIntegracaoDesfazer: [...this.selecionadas] });
    }

    public cancelar(): void {
        this.dialogRef.close();
    }
}
